import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, EmptyState, Icon, Spinner, Stat, Tabs } from "../ui";
import type { Project } from "../types";
import { ProjectRow } from "./ProjectRow";
import { NewProjectModal } from "./NewProjectModal";
import { ConfirmDeleteModal } from "../components/ConfirmDeleteModal";
import { useTweaks } from "../tweaks/useTweaks";
import { useProjects } from "../hooks/useProjects";
import { useDeleteProject } from "../hooks/useDeleteProject";
import { MOCK_PROJECTS } from "../data/mock";

type Filter = "all" | "active" | "complete";

export function ProjectsList() {
  const navigate = useNavigate();
  const [tweaks] = useTweaks();
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);

  const realQuery = useProjects(query.trim() || undefined, !tweaks.mock);
  const deleteMutation = useDeleteProject();

  const projects: Project[] = tweaks.mock ? MOCK_PROJECTS : realQuery.data ?? [];
  const isLoading = !tweaks.mock && realQuery.isPending;
  const isError = !tweaks.mock && realQuery.isError;

  const onOpen = (p: Project) => navigate(`/projects/${p.id}`);
  const onCreated = (created: Project) => {
    setShowNew(false);
    navigate(`/projects/${created.id}`);
  };
  const requestDelete = (p: Project) => setPendingDelete(p);
  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
    });
  };

  const filtered = projects.filter((p) => {
    if (filter === "active" && p.current_step >= 8) return false;
    if (filter === "complete" && p.current_step < 8) return false;
    if (query && !p.drug_api_name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: projects.length,
    active: projects.filter((p) => p.current_step >= 1 && p.current_step < 8).length,
    complete: projects.filter((p) => p.current_step >= 8).length,
  };

  return (
    <div className="min-h-screen anim-fade">
      <header className="border-b-2 border-[var(--ink)]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-baseline gap-6">
            <div className="display-5 leading-none">PDE Writer</div>
            <span className="caption hidden md:block">Altox · Toxicologia regulatória</span>
          </div>
          {/* <div className="flex items-center gap-4">
            <span className="caption hidden md:block">Acesso via FarmaHub</span>
          </div> */}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-10 sm:py-12">
        <div className="grid grid-cols-12 gap-10 mb-12 sm:mb-16">
          <div className="col-span-12 lg:col-span-8">
            <div className="eyebrow mb-4">
              Edição corrente ·{" "}
              {new Date().toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
            <h1 className="display-1 leading-[0.94] text-[var(--ink)]">
              Monografias
              <br />
              <em className="serif italic font-light">em curso.</em>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <div className="grid grid-cols-3 gap-6 border-t-2 border-[var(--ink)] pt-6">
              <Stat value={stats.total} label="Total" size="md" />
              <Stat value={stats.active} label="Em curso" size="md" />
              <Stat value={stats.complete} label="Entregues" size="md" />
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
          <Tabs<Filter>
            className="w-full overflow-x-auto sm:w-auto"
            value={filter}
            onChange={setFilter}
            tabs={[
              { value: "all", label: "Todas", count: projects.length },
              { value: "active", label: "Em curso", count: stats.active },
              { value: "complete", label: "Concluídas", count: stats.complete },
            ]}
          />
          <div className="flex w-full items-stretch gap-3 sm:w-auto sm:items-center">
            <div className="relative min-w-0 flex-1 sm:flex-none">
              <Icon.Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por IFA…"
                className="h-9 w-full pl-9 pr-3 bg-transparent border border-[var(--rule)] focus:border-[var(--ink)] text-[13px] transition-colors sm:w-56"
              />
            </div>
            <Button
              variant="ink"
              icon={Icon.Plus}
              onClick={() => setShowNew(true)}
              className="shrink-0"
            >
              Nova monografia
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-3 caption py-12 justify-center">
            <Spinner /> Carregando monografias…
          </div>
        ) : isError ? (
          <div
            role="alert"
            className="caption px-4 py-6 border border-[var(--strike)] text-[var(--strike)]"
          >
            Não foi possível carregar as monografias. Verifique sua conexão e a sessão.
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Icon.FileText}
            title="Nenhuma monografia"
            description="Comece criando uma nova monografia ou ajuste os filtros."
            action={
              <Button variant="ink" icon={Icon.Plus} onClick={() => setShowNew(true)}>
                Nova monografia
              </Button>
            }
          />
        ) : (
          <div className="border-t border-[var(--ink)]">
            <div className="hidden md:grid grid-cols-12 gap-6 py-3 border-b border-[var(--rule)] caption">
              <div className="col-span-1">№</div>
              <div className="col-span-4">Insumo farmacêutico ativo</div>
              <div className="col-span-3">Identificação</div>
              <div className="col-span-2">Etapa atual</div>
              <div className="col-span-1 text-right">Atualizado</div>
              <div className="col-span-1 text-right" />
            </div>
            <div className="anim-stagger">
              {filtered.map((p, i) => (
                <div key={p.id} className="relative group/row">
                  <ProjectRow idx={i + 1} project={p} onOpen={() => onOpen(p)} />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestDelete(p);
                    }}
                    aria-label={`Excluir ${p.drug_api_name}`}
                    title="Excluir monografia"
                    className="absolute right-2 top-5 caption text-[var(--ink-4)] hover:text-[var(--strike)] opacity-100 transition-opacity md:right-12 md:top-6 md:opacity-0 md:group-hover/row:opacity-100"
                  >
                    <Icon.Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <footer className="mt-24 pt-6 border-t border-[var(--rule)] flex items-center justify-between caption flex-wrap gap-4">
          <span>PDE Writer</span>
          <span>Suporte · suporte@altox.com.br</span>
        </footer>
      </main>

      {showNew && (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={onCreated}
        />
      )}
      <ConfirmDeleteModal
        open={!!pendingDelete}
        drugApiName={pendingDelete?.drug_api_name ?? ""}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
